import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from './payment';

/**
 * Certificate of Insurance (COI) data for one insured member — mirrors the server CoiData.
 * Reads may come back PascalCase, so mapCoi normalizes them. The on-screen view only consumes
 * the header fields (policyNo, names, dates); the full certificate is the server HTML.
 */
export interface CoiData {
  // member / insured person
  memberId: number;
  fullName?: string | null;
  age?: number | null;
  gender?: string | null;
  memberUniqueId?: string | null;
  dateOfBirth?: string | null;
  // issued policy
  policyNo?: string | null; // = certificate no; null => NOT yet issued (no COI)
  receiptNo?: string | null;
  policyStartDate?: string | null;
  coverageStartDate?: string | null;
  policyEndDate?: string | null;
  masterPolicyName?: string | null; // shown as "Insured" on the certificate
  masterPolicyNumber?: string | null;
  insuranceCompanyName?: string | null;
  // premium
  basePremium?: number | null;
  gstAmt?: number | null;
  gstPer?: number | null;
  grossAmt?: number | null;
  doctorConsultantAMT?: number | null;
  // insured group (Client_Master)
  clientName?: string | null;
  clientCompanyName?: string | null;
  clientAddress?: string | null;
  clientCity?: string | null;
  clientState?: string | null;
  clientPincode?: string | null;
  clientGstNo?: string | null;
  // intermediary (Partner)
  partnerName?: string | null;
  partnerCode?: string | null;
  // nominee
  nomineeName?: string | null;
  nomineeRelationship?: string | null;
  // benefit sums insured (PremiumRules_details)
  criticalIllness?: number | null;
  dailyHospitalisation?: number | null;
  icUhospitalization?: number | null;
  accidentalDeath?: number | null;
  permanentTotalDisablement?: number | null;
  doctorConsultant?: number | null;
  otherType?: string | null;
  other?: number | null;
}

/** The block ConfirmPayment / Razorpay Verify / UpdateStatus return on SUCCESS (next to `receipt`). */
export interface CoiRef {
  available: boolean;
  memberId: number;
  downloadUrl: string; // relative "/api/Coi/Download?memberId=123" — DON'T use as a raw href
  htmlUrl: string; // relative "/api/Coi/Html?memberId=123"
}

// ===================== queue-based COI delivery (POST api/Coi/Send) =====================

export type CoiChannel = 'EMAIL' | 'SMS' | 'WHATSAPP';

/** POST api/Coi/Send — render the COI once, store it, and QUEUE messages for the worker. */
export interface CoiSendRequest {
  memberId: number;
  /** Optional subset of EMAIL / SMS / WHATSAPP; omit ⇒ every channel the partner/client enabled. */
  channels?: string[];
  email?: string; // override recipient (else the member's own)
  mobile?: string;
  /** Which document(s) to send. Certificate defaults on; receipt is opt-in. */
  coi?: boolean;
  receipt?: boolean;
}

/** Send response `data` — channels are QUEUED (delivered out-of-band), never "sent". */
export interface CoiSendResult {
  memberId: number;
  policyNo: string;
  channels: string[]; // actually queued
  skipped: string[]; // annotated, e.g. "WHATSAPP (already queued)"
  enabledChannels: string[]; // what the partner/client has enabled
  fileName: string;
  url: string; // presigned COI link (what SMS/WhatsApp carry)
  linkExpiresInDays: number;
}

/** GET api/Coi/Channels?memberId= — what the Send dialog needs up front. */
export interface CoiChannels {
  memberId: number;
  issued: boolean;
  policyNo?: string | null;
  owner: 'client' | 'partner';
  enabledChannels: string[]; // channels the partner/client turned ON
  deliverable: string[]; // enabled AND the member has a matching email/mobile
  hasEmail: boolean;
  hasMobile: boolean;
}

// ===================== defensive PascalCase → camelCase mapping =====================

function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asNum(v: any, dflt = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}
function pickNum(x: any, ...keys: string[]): number | null {
  const v = pick(x, ...keys);
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}
function pickStr(x: any, ...keys: string[]): string | null {
  const v = pick(x, ...keys);
  return v == null ? null : String(v);
}

function mapCoi(x: any): CoiData {
  return {
    memberId: asNum(pick(x, 'MemberId', 'memberId')),
    fullName: pickStr(x, 'FullName', 'fullName'),
    age: pickNum(x, 'Age', 'age'),
    gender: pickStr(x, 'Gender', 'gender'),
    memberUniqueId: pickStr(x, 'MemberUniqueId', 'memberUniqueId'),
    dateOfBirth: pickStr(x, 'DateOfBirth', 'dateOfBirth'),

    policyNo: pickStr(x, 'PolicyNo', 'policyNo'),
    receiptNo: pickStr(x, 'ReceiptNo', 'receiptNo'),
    policyStartDate: pickStr(x, 'PolicyStartDate', 'policyStartDate'),
    coverageStartDate: pickStr(x, 'CoverageStartDate', 'coverageStartDate'),
    policyEndDate: pickStr(x, 'PolicyEndDate', 'policyEndDate'),
    masterPolicyName: pickStr(x, 'MasterPolicyName', 'masterPolicyName'),
    masterPolicyNumber: pickStr(x, 'MasterPolicyNumber', 'masterPolicyNumber'),
    insuranceCompanyName: pickStr(x, 'InsuranceCompanyName', 'insuranceCompanyName'),

    basePremium: pickNum(x, 'BasePremium', 'basePremium'),
    gstAmt: pickNum(x, 'GSTAmt', 'gstAmt', 'GstAmt', 'gSTAmt'),
    gstPer: pickNum(x, 'GSTPer', 'gstPer', 'GstPer', 'gSTPer'),
    grossAmt: pickNum(x, 'GrossAmt', 'grossAmt'),
    doctorConsultantAMT: pickNum(x, 'DoctorConsultantAMT', 'doctorConsultantAMT', 'doctorConsultantAmt'),

    clientName: pickStr(x, 'ClientName', 'clientName'),
    clientCompanyName: pickStr(x, 'ClientCompanyName', 'clientCompanyName'),
    clientAddress: pickStr(x, 'ClientAddress', 'clientAddress'),
    clientCity: pickStr(x, 'ClientCity', 'clientCity'),
    clientState: pickStr(x, 'ClientState', 'clientState'),
    clientPincode: pickStr(x, 'ClientPincode', 'clientPincode'),
    clientGstNo: pickStr(x, 'ClientGstNo', 'clientGstNo'),

    partnerName: pickStr(x, 'PartnerName', 'partnerName'),
    partnerCode: pickStr(x, 'PartnerCode', 'partnerCode'),

    nomineeName: pickStr(x, 'NomineeName', 'nomineeName'),
    nomineeRelationship: pickStr(x, 'NomineeRelationship', 'nomineeRelationship'),

    criticalIllness: pickNum(x, 'CriticalIllness', 'criticalIllness'),
    dailyHospitalisation: pickNum(x, 'DailyHospitalisation', 'dailyHospitalisation'),
    icUhospitalization: pickNum(x, 'ICUhospitalization', 'icUhospitalization', 'icuHospitalization', 'IcuHospitalization'),
    accidentalDeath: pickNum(x, 'AccidentalDeath', 'accidentalDeath'),
    permanentTotalDisablement: pickNum(x, 'PermanentTotalDisablement', 'permanentTotalDisablement'),
    doctorConsultant: pickNum(x, 'DoctorConsultant', 'doctorConsultant'),
    otherType: pickStr(x, 'OtherType', 'otherType'),
    other: pickNum(x, 'Other', 'other'),
  };
}

/** Apply mapCoi to a single-object envelope `data`, passing everything else through. */
function mapEnv(res: any): ApiEnvelope<CoiData> {
  if (res?.data && typeof res.data === 'object' && !Array.isArray(res.data)) {
    return { ...res, data: mapCoi(res.data) };
  }
  return res;
}

function pickArr(x: any, ...keys: string[]): string[] {
  const v = pick(x, ...keys);
  return Array.isArray(v) ? v.map(String) : [];
}

function mapSendResult(x: any): CoiSendResult {
  return {
    memberId: asNum(pick(x, 'MemberId', 'memberId')),
    policyNo: pickStr(x, 'PolicyNo', 'policyNo') ?? '',
    channels: pickArr(x, 'Channels', 'channels'),
    skipped: pickArr(x, 'Skipped', 'skipped'),
    enabledChannels: pickArr(x, 'EnabledChannels', 'enabledChannels'),
    fileName: pickStr(x, 'FileName', 'fileName') ?? '',
    url: pickStr(x, 'Url', 'url') ?? '',
    linkExpiresInDays: asNum(pick(x, 'LinkExpiresInDays', 'linkExpiresInDays')),
  };
}

function mapChannels(x: any): CoiChannels {
  return {
    memberId: asNum(pick(x, 'MemberId', 'memberId')),
    issued: pick(x, 'Issued', 'issued') === true,
    policyNo: pickStr(x, 'PolicyNo', 'policyNo'),
    owner: (pickStr(x, 'Owner', 'owner') === 'client' ? 'client' : 'partner') as 'client' | 'partner',
    enabledChannels: pickArr(x, 'EnabledChannels', 'enabledChannels'),
    deliverable: pickArr(x, 'Deliverable', 'deliverable'),
    hasEmail: pick(x, 'HasEmail', 'hasEmail') === true,
    hasMobile: pick(x, 'HasMobile', 'hasMobile') === true,
  };
}

/**
 * COI endpoints (api/Coi). Data* → JSON envelope (any time, gate on `policyNo`); Html* → text/html
 * (issued only → 400); Download* → application/pdf blob (issued only → 400; 500 on a render error).
 * Keyed by memberId (primary) or policyNo. NEVER point an <a href>/iframe src at these — the JWT
 * wouldn't be attached; always fetch via HttpClient (the interceptor adds Authorization).
 */
@Injectable({ providedIn: 'root' })
export class CoiService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl;

  coiData(memberId: number) {
    return this.http
      .get<ApiEnvelope<CoiData>>(`${this.api}/Coi/Data`, { params: { memberId } })
      .pipe(map(mapEnv));
  }
  coiDataByPolicy(policyNo: string) {
    return this.http
      .get<ApiEnvelope<CoiData>>(`${this.api}/Coi/DataByPolicyNo`, { params: { policyNo } })
      .pipe(map(mapEnv));
  }

  coiHtml(memberId: number) {
    return this.http.get(`${this.api}/Coi/Html`, { params: { memberId }, responseType: 'text' });
  }
  coiHtmlByPolicy(policyNo: string) {
    return this.http.get(`${this.api}/Coi/HtmlByPolicyNo`, { params: { policyNo }, responseType: 'text' });
  }

  /** COI PDF. Default = combined welcome-letter + COI (partner members); coiOnly=true = certificate alone. */
  coiPdf(memberId: number, coiOnly = false) {
    const params: any = { memberId };
    if (coiOnly) params.coiOnly = true;
    return this.http.get(`${this.api}/Coi/Download`, { params, responseType: 'blob' });
  }

  /** Welcome letter ONLY (partner-onboarded, issued members). */
  welcomePdf(memberId: number) {
    return this.http.get(`${this.api}/Coi/WelcomeDownload`, { params: { memberId }, responseType: 'blob' });
  }
  coiPdfByPolicy(policyNo: string) {
    return this.http.get(`${this.api}/Coi/DownloadByPolicyNo`, { params: { policyNo }, responseType: 'blob' });
  }

  /** Queue the COI to the customer over the partner/client-ENABLED channels (server-authoritative).
   *  NOTE: unlike most endpoints here, CoiController returns REAL HTTP statuses — failures
   *  (400 not-issued / no-channel, 404 member, 409 already-in-flight) arrive on the error
   *  callback with the envelope in `err.error`. */
  sendCoi(body: CoiSendRequest) {
    return this.http
      .post<ApiEnvelope<CoiSendResult>>(`${this.api}/Coi/Send`, body)
      .pipe(
        map((res: any) =>
          res?.data && typeof res.data === 'object' ? { ...res, data: mapSendResult(res.data) } : res,
        ),
      );
  }

  /** Pre-fetch a member's sendable channels so the Send dialog can pre-select / grey out. */
  getCoiChannels(memberId: number) {
    return this.http
      .get<ApiEnvelope<CoiChannels>>(`${this.api}/Coi/Channels`, { params: { memberId } })
      .pipe(
        map((res: any) =>
          res?.data && typeof res.data === 'object' ? { ...res, data: mapChannels(res.data) } : res,
        ),
      );
  }
}
